import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { NavBar, Tag, Tabs, Input, Button, Loading, Empty, ActionSheet, Toast, Dialog } from 'react-vant'
import { ArrowLeft, ArrowDown, Upgrade, Success, Failure, Delete } from '@react-vant/icons'
import request from '../../api/request'

// 与后端 services/exemption_policy.CATEGORY_LABEL 对齐
const EXEMPTION_CATEGORY_LABEL = {
    exempt_illness: '因病免测',
    exempt_disability: '残疾免测',
    deferred_exempt: '缓测转免测',
    exempt_injury: '急性伤病（缓测）',
    deferred: '因故缓测',
    selective: '免考选测',
}

export default function ExemptionApply() {
    const navigate = useNavigate()
    const location = useLocation()
    const fileInputRef = useRef(null)

    // Parse student_id from query if present
    const queryParams = new URLSearchParams(location.search)
    const urlStudentId = queryParams.get('student_id')

    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [uploadLoading, setUploadLoading] = useState(false)
    
    const [children, setChildren] = useState([])
    const [selectedChild, setSelectedChild] = useState(null)
    const [currentYear, setCurrentYear] = useState('')
    const [activeTab, setActiveTab] = useState('apply') // 'apply', 'history'

    // Form inputs
    const [reason, setReason] = useState('')
    const [category, setCategory] = useState('exempt_illness')
    const [uploadedFileUrl, setUploadedFileUrl] = useState('')
    
    // History list
    const [historyList, setHistoryList] = useState([])
    const [showChildPicker, setShowChildPicker] = useState(false)

    // 1. Fetch children and current year on mount
    useEffect(() => {
        const initData = async () => {
            setLoading(true)
            try {
                // Fetch current year
                const yearRes = await request.get('/academic-year/current/')
                if (yearRes && yearRes.current_academic_year) {
                    setCurrentYear(yearRes.current_academic_year)
                }

                // Fetch children
                const res = await request.get('/academic/students/m/children/')
                if (res.success && res.data && res.data.length > 0) {
                    setChildren(res.data)
                    const targetChild = urlStudentId 
                        ? res.data.find(c => String(c.id) === String(urlStudentId)) 
                        : null
                    setSelectedChild(targetChild || res.data[0])
                }
            } catch (e) {
                console.error('初始化数据失败:', e)
            } finally {
                setLoading(false)
            }
        }
        initData()
    }, [urlStudentId])

    // 2. Fetch history when selectedChild changes or tab changes
    const fetchHistory = async () => {
        if (!selectedChild) return
        setLoading(true)
        try {
            const res = await request.get('/sports/exemptions/', {
                params: {
                    student_id: selectedChild.id
                }
            })
            if (res && res.items) {
                setHistoryList(res.items)
            } else {
                setHistoryList([])
            }
        } catch (e) {
            console.error('获取历史记录失败:', e)
            setHistoryList([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (activeTab === 'history') {
            fetchHistory()
        }
    }, [selectedChild, activeTab])

    // Child picker actions
    const pickerActions = children.map(child => ({
        name: child.real_name,
        subname: child.class_name || '未分配班级',
        value: child.id,
        color: selectedChild?.id === child.id ? 'var(--primary-green)' : undefined,
    }))

    const handleSelectChild = (action) => {
        const child = children.find(c => c.id === action.value)
        setSelectedChild(child)
        setShowChildPicker(false)
        // Reset form when child switches
        setReason('')
        setUploadedFileUrl('')
    }

    // Trigger local file selection
    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    // File Upload Handler
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        // Validate size (max 5MB)
        if (file.size / 1024 / 1024 > 5) {
            Toast.fail('诊断证明证明图片不能大于 5MB')
            return
        }

        const formData = new FormData()
        formData.append('file', file)
        setUploadLoading(true)

        try {
            // Retrieve token from request wrapper standard source
            const token = localStorage.getItem('token')
            // Using standard fetch/axios directly for upload to ensure headers are accurate
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/sports/exemptions/upload-certificate/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            })
            const result = await res.json()
            if (result.success && result.url_path) {
                setUploadedFileUrl(result.url_path)
                Toast.success('医学证明上传成功')
            } else {
                Toast.fail(result.detail || '文件上传接口异常')
            }
        } catch (err) {
            console.error('上传文件失败:', err)
            Toast.fail('服务器连接失败，请稍后重试')
        } finally {
            setUploadLoading(false)
            // Reset input value so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    // Submit Exemption Form
    const handleSubmit = async () => {
        if (!selectedChild) {
            Toast.info('未选定申报学生')
            return
        }
        if (!reason.trim()) {
            Toast.info('请填写申请免测原因')
            return
        }

        setSubmitLoading(true)
        try {
            const res = await request.post('/sports/exemptions/', {
                student_id: selectedChild.id,
                academic_year: currentYear,
                // 之前没传类别 → 每条申请都落成「非残疾 60 分」的旧口径，
                // 老师审批时还得回头猜这是免测还是缓测。类别必须由申请人先声明。
                exemption_category: category,
                reason: reason,
                medical_certificate: uploadedFileUrl
            })
            if (res && res.id) {
                Toast.success('免测申请提交成功')
                // Clear form
                setReason('')
                setUploadedFileUrl('')
                // Switch to history
                setActiveTab('history')
            }
        } catch (e) {
            console.error('提交免测申请失败:', e)
            Toast.fail(e.message || '提交免测申请失败')
        } finally {
            setSubmitLoading(false)
        }
    }

    // Delete Exemption record
    const handleDeleteRecord = (id) => {
        Dialog.confirm({
            title: '确认删除',
            message: '确定要撤销并删除此条免测申请记录吗？',
            onConfirm: async () => {
                try {
                    const res = await request.delete(`/sports/exemptions/${id}/`)
                    if (res && res.success) {
                        Toast.success('申请记录撤销成功')
                        fetchHistory()
                    }
                } catch (e) {
                    console.error('删除记录失败:', e)
                }
            }
        })
    }

    return (
        <div style={{ background: '#f5f8f6', minHeight: '100vh', paddingBottom: 40 }}>
            {/* NavBar */}
            {!window.ReactNativeWebView && (
                <NavBar
                    title="体测免测申请"
                    leftArrow={<ArrowLeft />}
                    onClickLeft={() => navigate(-1)}
                    fixed
                    placeholder
                />
            )}

            {/* Student Switcher Banner */}
            <div style={{ background: 'linear-gradient(to right, #00b578, #06ad56)', padding: '16px', color: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 18, fontWeight: 800 }}>{selectedChild?.real_name}</span>
                            <Tag style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff' }}>
                                {selectedChild?.gender === 'male' || selectedChild?.gender === '男' ? '男' : '女'}
                            </Tag>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                            {selectedChild?.class_name || '未分配班级'}
                        </div>
                    </div>

                    {children.length > 1 && (
                        <Button
                            size="small"
                            onClick={() => setShowChildPicker(true)}
                            style={{
                                background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                                borderRadius: 14, height: 28, fontSize: 12, fontWeight: 600
                            }}
                            icon={<ArrowDown style={{ fontSize: 10 }} />}
                        >
                            切换学生
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs Selector */}
            <div style={{ margin: '12px 14px' }}>
                <Tabs active={activeTab} onChange={setActiveTab} color="#00b578">
                    <Tabs.TabPane name="apply" title="申请免测" />
                    <Tabs.TabPane name="history" title="申请记录" />
                </Tabs>
            </div>

            {/* Content Area */}
            {loading && !submitLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                    <Loading type="spinner" color="#00b578" />
                </div>
            ) : (
                <div style={{ padding: '0 14px' }}>
                    {/* TAB 1: Apply Form */}
                    {activeTab === 'apply' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            
                            {/* Academic Year Info */}
                            <div style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                    <span style={{ color: '#94a3b8', fontWeight: 500 }}>申请学年次：</span>
                                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{currentYear ? `${currentYear} 学年` : '加载中...'}</span>
                                </div>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>
                                    免测只针对当前学年，历史学年不可再申请
                                </div>
                            </div>

                            {/* Category Picker */}
                            <div style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                                    申请类别
                                </div>
                                <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 12 }}>
                                    重大疾病、残疾经审批后按满分计入且不计入统计；其余情况按缓测处理，不产生成绩、仍需补测。
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {[
                                        { key: 'exempt_illness', label: '因病免测', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
                                        { key: 'exempt_disability', label: '残疾免测', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
                                        { key: 'exempt_injury', label: '急性伤病', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
                                        { key: 'deferred', label: '因故缓测', color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' },
                                    ].map((opt) => {
                                        const isActive = category === opt.key
                                        return (
                                            <div
                                                key={opt.key}
                                                onClick={() => setCategory(opt.key)}
                                                style={{
                                                    padding: '7px 12px',
                                                    borderRadius: 8,
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    color: isActive ? opt.color : '#64748b',
                                                    background: isActive ? opt.bg : '#F8FAFC',
                                                    border: `1px solid ${isActive ? opt.border : '#E2E8F0'}`,
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Reason Description Input */}
                            <div style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>
                                    免测原因 / 疾病及异常说明
                                </div>
                                <Input
                                    type="textarea"
                                    rows={4}
                                    placeholder="请详细说明学生的免测事由。例如：先天性心脏病、近期下肢骨折需静养或医生诊断不宜做剧烈运动等..."
                                    value={reason}
                                    onChange={setReason}
                                    style={{ background: '#f8fafc', borderRadius: 8, padding: 10, fontSize: 13 }}
                                />
                            </div>

                            {/* Medical Certificate Upload */}
                            <div style={{ background: '#fff', borderRadius: 16, padding: '16px' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                                    医学诊断证明附件 (上传)
                                </div>
                                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
                                    请上传医院出具的盖章诊断证明或病历原件拍照图片
                                </div>

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />

                                {uploadedFileUrl ? (
                                    <div style={{ position: 'relative', width: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <img
                                            src={`${import.meta.env.VITE_API_BASE_URL || '/api'}${uploadedFileUrl}`}
                                            alt="诊断证明"
                                            style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 300, objectFit: 'contain' }}
                                        />
                                        <div
                                            onClick={() => setUploadedFileUrl('')}
                                            style={{
                                                position: 'absolute', top: 12, right: 12,
                                                width: 32, height: 32, borderRadius: '50%',
                                                background: 'rgba(0,0,0,0.6)', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center', color: '#fff'
                                            }}
                                        >
                                            <Delete style={{ fontSize: 16 }} />
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        onClick={triggerFileSelect}
                                        style={{
                                            border: '2px dashed #d1d5db',
                                            borderRadius: 12,
                                            height: 120,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#f8fafc',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {uploadLoading ? (
                                            <Loading type="spinner" color="#00b578" />
                                        ) : (
                                            <>
                                                <Upgrade style={{ fontSize: 28, color: '#94a3b8', marginBottom: 8 }} />
                                                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>点击选择文件拍照上传</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <Button
                                block
                                round
                                type="primary"
                                loading={submitLoading}
                                onClick={handleSubmit}
                                style={{
                                    marginTop: 10,
                                    background: 'linear-gradient(135deg, #00b578 0%, #06ad56 100%)',
                                    border: 'none',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    height: 44,
                                    boxShadow: '0 4px 16px rgba(0, 181, 120, 0.25)'
                                }}
                            >
                                提交免测申请
                            </Button>
                        </div>
                    )}

                    {/* TAB 2: History List */}
                    {activeTab === 'history' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {historyList.length === 0 ? (
                                <Empty description="暂无免测申请记录" style={{ padding: '60px 0' }} />
                            ) : (
                                historyList.map(record => {
                                    const statusConfig = {
                                        approved: { color: 'green', label: '已同意', icon: <Success /> },
                                        rejected: { color: 'red', label: '已驳回', icon: <Failure /> },
                                        revoked: { color: 'default', label: '已撤销', icon: null },
                                        pending: { color: 'orange', label: '审核中', icon: null }
                                    }[record.status] || { color: 'blue', label: record.status, icon: null }

                                    const categoryLabel = EXEMPTION_CATEGORY_LABEL[record.exemption_category] || ''
                                    const isDeferredKind = ['deferred', 'exempt_injury'].includes(record.exemption_category)

                                    return (
                                        <div
                                            key={record.id}
                                            style={{
                                                background: '#fff',
                                                borderRadius: 16,
                                                padding: '16px',
                                                borderLeft: `5px solid ${
                                                    record.status === 'approved' ? '#00b578' :
                                                    record.status === 'rejected' ? '#e11d48' : '#fa8c16'
                                                }`
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b' }}>
                                                    {record.academic_year} 学年
                                                    {categoryLabel ? ` · ${categoryLabel}` : ''}
                                                </span>
                                                <Tag type={statusConfig.color} style={{ borderRadius: 6 }}>
                                                    {statusConfig.label}
                                                </Tag>
                                            </div>

                                            {/* 口径说明：免测计入分数不计入统计；缓测无分数仍需补测 */}
                                            {record.status === 'approved' && (
                                                <div style={{
                                                    fontSize: 12,
                                                    marginBottom: 10,
                                                    padding: '7px 10px',
                                                    borderRadius: 8,
                                                    color: isDeferredKind ? '#92400e' : '#5b21b6',
                                                    background: isDeferredKind ? '#fffbeb' : '#f5f3ff',
                                                }}>
                                                    {isDeferredKind
                                                        ? '缓测：本学年不产生体测成绩，仍计入应测人数，请在期限内补测。'
                                                        : record.score_policy === 'teacher_fold'
                                                        ? `免测：按教师折算 ${record.folded_score ?? '--'} 分计入，不计入统计。`
                                                        : '免测：按满分 100 分计入，不计入统计。'}
                                                </div>
                                            )}

                                            <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600, marginBottom: 10 }}>
                                                事由：{record.reason}
                                            </div>

                                            {/* Proof Image */}
                                            {record.medical_certificate && (
                                                <div style={{ marginBottom: 12 }}>
                                                    <span style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 6 }}>诊断证明：</span>
                                                    <img
                                                        src={`${import.meta.env.VITE_API_BASE_URL || '/api'}${record.medical_certificate}`}
                                                        alt="诊断证明"
                                                        onClick={() => window.open(`${import.meta.env.VITE_API_BASE_URL || '/api'}${record.medical_certificate}`, '_blank')}
                                                        style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                                                    />
                                                </div>
                                            )}

                                            {/* Audit opinion */}
                                            {record.status !== 'pending' && (
                                                <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, fontSize: 12, color: '#475569', marginTop: 10 }}>
                                                    <div style={{ fontWeight: 'bold', color: '#334155', marginBottom: 4 }}>审核批复：</div>
                                                    <div>{record.audit_opinion || '准予免测。'}</div>
                                                    <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 6, textAlign: 'right' }}>
                                                        审核人: {record.auditor_name || '校医/老师'}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action button */}
                                            {record.status === 'pending' && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
                                                    <Button
                                                        size="small"
                                                        plain
                                                        type="danger"
                                                        style={{ borderRadius: 8 }}
                                                        onClick={() => handleDeleteRecord(record.id)}
                                                    >
                                                        撤销申请
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Child Switcher ActionSheet */}
            <ActionSheet
                visible={showChildPicker}
                actions={pickerActions}
                onCancel={() => setShowChildPicker(false)}
                onSelect={handleSelectChild}
                cancelText="取消"
                description="请选择学生"
            />
        </div>
    )
}
